import types from '../mutation-types';
import exotelWebPhone from 'dashboard/helper/exotelWebPhone';
import ContactAPI from 'dashboard/api/contacts';
import {
  REGISTRATION_STATES,
  CALL_STATES,
  CALL_DIRECTIONS,
} from 'dashboard/constants/calling';

/**
 * State for the Exotel Web Calling softphone.
 *
 * The softphone itself is a singleton living outside Vuex (it owns a WebRTC
 * session that must survive route changes); this module holds the observable
 * projection of it that the widget renders.
 */
export const state = {
  registrationState: REGISTRATION_STATES.UNREGISTERED,
  callState: CALL_STATES.IDLE,
  activeCall: null,
  isMuted: false,
  isOnHold: false,
  // False when another browser tab already holds the softphone lease.
  hasPhoneLease: true,
  // Conversation the in-flight outbound call belongs to, captured before
  // dialling so call logs can be attributed once Exotel reports the call.
  outboundContext: null,
  // Why the softphone could not start, when the server told us.
  errorMessage: '',
  // False once the server says this agent has no Exotel user. Web Calling is
  // switched on per account but provisioned per agent, so an unprovisioned
  // agent is a normal state, not a failure: the widget stays hidden and their
  // Call button keeps using click-to-call.
  isProvisioned: true,
  // Armed before an outbound dial. Exotel places an outbound call by ringing
  // the agent's own browser first, which the SDK reports as an ordinary
  // `incoming` event; this flag lets that leg be recognised as ours and
  // answered automatically instead of being shown as a call to accept.
  pendingOutbound: false,
};

// How long an armed outbound dial waits for its agent leg to ring back
// before giving up on correlation.
const PENDING_OUTBOUND_WINDOW = 20000;
let pendingOutboundTimer = null;

export const getters = {
  getRegistrationState: _state => _state.registrationState,
  getCallState: _state => _state.callState,
  getActiveCall: _state => _state.activeCall,
  getOutboundContext: _state => _state.outboundContext,
  isMuted: _state => _state.isMuted,
  isOnHold: _state => _state.isOnHold,
  hasPhoneLease: _state => _state.hasPhoneLease,
  getErrorMessage: _state => _state.errorMessage,
  isProvisioned: _state => _state.isProvisioned,
  isRegistered: _state =>
    _state.registrationState === REGISTRATION_STATES.REGISTERED,
  isOnCall: _state => _state.callState !== CALL_STATES.IDLE,
};

export const actions = {
  beginSession({ commit }) {
    commit(types.SET_CALLING_ERROR, '');
    commit(types.SET_CALLING_PROVISIONED, true);
    commit(
      types.SET_CALLING_REGISTRATION_STATE,
      REGISTRATION_STATES.REGISTERING
    );
  },

  failSession({ commit }, { message } = {}) {
    commit(types.SET_CALLING_ERROR, message || '');
    commit(types.SET_CALLING_REGISTRATION_STATE, REGISTRATION_STATES.FAILED);
  },

  markUnprovisioned({ commit }) {
    commit(types.SET_CALLING_PROVISIONED, false);
    commit(
      types.SET_CALLING_REGISTRATION_STATE,
      REGISTRATION_STATES.UNREGISTERED
    );
  },

  async initialize({ commit, dispatch }, { accessToken, userId }) {
    if (exotelWebPhone.isConnected) return;

    // Only one tab may register the agent's SIP user; the others show the
    // widget as inactive rather than fighting over incoming calls.
    if (!exotelWebPhone.claimLease()) {
      commit(types.SET_CALLING_PHONE_LEASE, false);
      return;
    }
    commit(types.SET_CALLING_PHONE_LEASE, true);

    await exotelWebPhone.connect({
      accessToken,
      userId,
      onRegistrationEvent: (registrationState, error) => {
        // The helper passes the underlying exception on failure — a chunk
        // that would not load, an SDK that threw during Initialize — and
        // that is far more useful to show than a generic network hint.
        if (registrationState === REGISTRATION_STATES.FAILED && error) {
          commit(types.SET_CALLING_ERROR, error.message || String(error));
        }
        commit(types.SET_CALLING_REGISTRATION_STATE, registrationState);
      },
      onCallEvent: call => dispatch('handleCallEvent', call),
    });
  },

  teardown({ commit }) {
    exotelWebPhone.disconnect();
    commit(
      types.SET_CALLING_REGISTRATION_STATE,
      REGISTRATION_STATES.UNREGISTERED
    );
    commit(types.SET_CALLING_ACTIVE_CALL, null);
  },

  handleCallEvent({ commit, dispatch, state: _state }, call) {
    // Hold and mute arrive as toggles on the existing call rather than as
    // state transitions, so they are applied without touching call state.
    if (call.toggle) {
      commit(types.SET_CALLING_TOGGLES, {
        isMuted:
          call.toggle === 'mutetoggle' ? !_state.isMuted : _state.isMuted,
        isOnHold:
          call.toggle === 'holdtoggle' ? !_state.isOnHold : _state.isOnHold,
      });
      return;
    }

    if (call.state === CALL_STATES.IDLE) {
      commit(types.SET_CALLING_ACTIVE_CALL, null);
      return;
    }

    // The agent leg of a dial we just placed rings back as `incoming`.
    // Adopt it: keep it labelled as the outbound call to the number the agent
    // dialled, and answer it so Exotel proceeds to ring the customer.
    if (call.state === CALL_STATES.INCOMING && _state.pendingOutbound) {
      commit(types.SET_CALLING_PENDING_OUTBOUND, false);
      commit(types.SET_CALLING_ACTIVE_CALL, {
        ...call,
        direction: CALL_DIRECTIONS.OUTBOUND,
        remoteNumber: _state.activeCall?.remoteNumber || call.remoteNumber,
        remoteName: _state.activeCall?.remoteName || '',
        state: CALL_STATES.OUTGOING,
      });
      exotelWebPhone.acceptCall();
      return;
    }

    // `connected` on an outbound call means the agent's leg is up; the
    // customer may still be ringing, which only the status callback can tell
    // us. The label in the widget is worded accordingly.
    if (
      call.state === CALL_STATES.CONNECTED &&
      _state.activeCall?.direction === CALL_DIRECTIONS.OUTBOUND
    ) {
      commit(types.SET_CALLING_ACTIVE_CALL, {
        ..._state.activeCall,
        state: CALL_STATES.CONNECTED,
      });
      return;
    }

    commit(types.SET_CALLING_ACTIVE_CALL, call);

    if (call.state === CALL_STATES.INCOMING) {
      dispatch('resolveCallerContact', call);
    }
  },

  /**
   * Looks the caller up among the account's contacts so the widget can show
   * who is ringing and link to their profile. Matched on the last ten digits
   * so it survives the +91 / leading-0 variations Exotel and contacts use.
   */
  async resolveCallerContact({ commit, state: _state }, call) {
    const digits = String(call.remoteNumber || '').replace(/\D/g, '');
    if (digits.length < 10) return;
    const query = digits.slice(-10);

    try {
      const { data } = await ContactAPI.search(query);
      const contact = (data?.payload || []).find(candidate =>
        String(candidate.phone_number || '')
          .replace(/\D/g, '')
          .endsWith(query)
      );
      // The call may have ended, or another one started, while we searched.
      if (!contact || _state.activeCall?.callId !== call.callId) return;
      commit(types.SET_CALLING_CALLER_CONTACT, {
        id: contact.id,
        name: contact.name,
        thumbnail: contact.thumbnail,
      });
    } catch (error) {
      // Unknown caller is a normal outcome; the widget keeps showing the number.
    }
  },

  acceptCall() {
    exotelWebPhone.acceptCall();
  },

  hangupCall() {
    exotelWebPhone.hangupCall();
  },

  toggleMute() {
    exotelWebPhone.toggleMute();
  },

  toggleHold() {
    exotelWebPhone.toggleHold();
  },

  sendDtmf(_, digit) {
    exotelWebPhone.sendDtmf(digit);
  },

  /**
   * Dials a number from the softphone. The conversation context is stored
   * first so that whatever Exotel returns for the dial can be attributed even
   * if the agent navigates away before the call connects.
   */
  async startOutboundCall({ commit }, { number, conversation, name }) {
    commit(types.SET_CALLING_OUTBOUND_CONTEXT, conversation || null);
    commit(types.SET_CALLING_ACTIVE_CALL, {
      state: CALL_STATES.OUTGOING,
      direction: CALL_DIRECTIONS.OUTBOUND,
      remoteNumber: number,
      remoteName: name || '',
    });

    // Armed *before* the request: the agent leg can ring back faster than
    // the dial request resolves.
    commit(types.SET_CALLING_PENDING_OUTBOUND, true);
    clearTimeout(pendingOutboundTimer);
    pendingOutboundTimer = setTimeout(() => {
      commit(types.SET_CALLING_PENDING_OUTBOUND, false);
    }, PENDING_OUTBOUND_WINDOW);

    try {
      return await exotelWebPhone.makeCall(number);
    } catch (error) {
      commit(types.SET_CALLING_PENDING_OUTBOUND, false);
      commit(types.SET_CALLING_ACTIVE_CALL, null);
      commit(types.SET_CALLING_OUTBOUND_CONTEXT, null);
      throw error;
    }
  },
};

export const mutations = {
  [types.SET_CALLING_REGISTRATION_STATE]($state, registrationState) {
    $state.registrationState = registrationState;
  },
  [types.SET_CALLING_ACTIVE_CALL]($state, call) {
    $state.activeCall = call;
    $state.callState = call ? call.state : CALL_STATES.IDLE;
    if (!call) {
      $state.isMuted = false;
      $state.isOnHold = false;
      $state.outboundContext = null;
    }
  },
  [types.SET_CALLING_TOGGLES]($state, { isMuted, isOnHold }) {
    $state.isMuted = isMuted;
    $state.isOnHold = isOnHold;
  },
  [types.SET_CALLING_PHONE_LEASE]($state, hasPhoneLease) {
    $state.hasPhoneLease = hasPhoneLease;
  },
  [types.SET_CALLING_OUTBOUND_CONTEXT]($state, outboundContext) {
    $state.outboundContext = outboundContext;
  },
  [types.SET_CALLING_CALLER_CONTACT]($state, contact) {
    if (!$state.activeCall) return;
    $state.activeCall = { ...$state.activeCall, contact };
  },
  [types.SET_CALLING_PENDING_OUTBOUND]($state, pendingOutbound) {
    $state.pendingOutbound = pendingOutbound;
  },
  [types.SET_CALLING_ERROR]($state, errorMessage) {
    $state.errorMessage = errorMessage;
  },
  [types.SET_CALLING_PROVISIONED]($state, isProvisioned) {
    $state.isProvisioned = isProvisioned;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
